import React from "react";
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Download, ExternalLink } from 'lucide-react';
import GitGraph from "./Graph.png";
import contri from "./contri.png";
import "./About.css";

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const handleDownloadResume = () => {
    const link = document.createElement('a');
    link.href = '/resume.pdf'; 
    link.download = 'Aditya_Jaiswal_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.6, ease: "easeOut" } 
    }
  };

  return (
    <section id="about" ref={ref}>
      <div className="wrapper">
        <motion.div
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{ visible: { transition: { staggerChildren: 0.2 } } }}
        >
          
          {/* --- HEADER --- */}
          <motion.div className="title" variants={fadeInUp}>
            <h3>About Me</h3>
            <p className="separator" />
          </motion.div>

          {/* --- BIO SECTION --- */}
          <motion.div className="desc full" variants={fadeInUp}>
            <h4 className="subtitle">Engineering Scalable Systems & AI Solutions.</h4>
            <p>
              I am a <strong>Final Year IT Engineering Student</strong> with a focus on Full Stack Development and <strong>AI Integration</strong>. 
              My passion lies in bridging the gap between robust backend architectures and intuitive frontend experiences.
            </p>
            <p>
              Beyond standard web apps, I am actively building <strong>RAG (Retrieval-Augmented Generation)</strong> systems 
              and optimizing algorithms (DSA) to solve complex data problems. I don't just write code; I build products that 
              solve real-world risks—like my work on <em>Legal Risk Analysis</em> systems.
            </p>
            <p>
              When I'm not configuring <strong>AWS</strong> environments or optimizing <strong>MongoDB</strong> queries, 
              you can find me speed-cubing or analyzing strategy on the Chess board.
            </p>

            <motion.div 
              className="btn-container"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
            >
              <button
                className="download-resume-btn"
                onClick={handleDownloadResume}
              >
                <Download size={18} style={{ marginRight: '8px' }} />
                Download Resume
              </button>
            </motion.div>
          </motion.div>

          {/* --- TECH STACK (THE HERO SECTION) --- */}
          <motion.div className="tech-stack-container" variants={fadeInUp}>
            <div className="title" style={{ marginTop: "4rem" }}>
              <h3>Technical Arsenal</h3>
              <p className="separator" />
              <p className="subtitle" style={{ marginTop: "1rem" }}>
                My weapon of choice for building modern digital products.
              </p>
            </div>

            {/* Row 1: Core Development Stack */}
            <div className="skill-row">
              <h5 className="skill-row-title">Core Stack</h5>
              <motion.img 
                className="skill-strip"
                src="https://skillicons.dev/icons?i=react,nextjs,nodejs,express,mongodb,postgres,typescript,tailwind"
                alt="Core Tech Stack"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </div>

            {/* Row 2: Languages, Cloud & Tools */}
            <div className="skill-row">
              <h5 className="skill-row-title">Languages & Infrastructure</h5>
              <motion.img 
                className="skill-strip"
                src="https://skillicons.dev/icons?i=python,java,django,aws,git,postman,linux,docker"
                alt="Tools and Infra"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
            </div>
          </motion.div>

          {/* --- GITHUB STATS --- */}
          <motion.div 
            className="github-section"
            variants={fadeInUp}
            style={{ marginTop: "5rem" }}
          >
            <div className="title">
              <h3>GitHub Activity</h3>
              <p className="separatorGithub" />
            </div>

            <div className="github-grid">
              <motion.a 
                href="https://github.com/AdityaCJaiswal" 
                target="_blank"
                className="graph-container"
                whileHover={{ y: -5 }}
              >
                <img className="GraphImg" src={GitGraph} alt="Contribution Graph" />
              </motion.a>
              
              <motion.a 
                 href="https://github.com/AdityaCJaiswal" 
                 target="_blank"
                 className="graph-container"
                 whileHover={{ y: -5 }}
              >
                <img className="GraphImg" src={contri} alt="Contribution Stats" />
              </motion.a>
            </div>
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};

export default About;